// Path in repo: src/pages/organization-dashboard/components/AnalyticsPanel.jsx
//
// Renders for the Intelligence > Overview tab. Fetches real data via
// dashboardAnalyticsHelpers (the same listCourses/listOrgEnrollments calls
// the main Dashboard KPI row already uses) instead of organization.analytics
// mocks. TopCountries stays on fallback data — no country field exists yet
// on enrollments/users/courses (see chat for context).

import React, { useState, useEffect, useCallback } from "react"
import LearnersChart from "./analytics/LearnersChart"
import EnrolledNow from "./analytics/EnrolledNow"
import TopCourses from "./analytics/TopCourses"
import TopCountries from "./analytics/TopCountries"
import LearnerMix from "./analytics/LearnerMix"
import EnrollmentByCategory from "./analytics/EnrollmentByCategory"
import InsightsStrip from "./analytics/InsightsStrip"
import {
  getLearnerTrend,
  getTopCourses,
  getEnrollmentByCategory,
  getLearnerMix,
  getActiveNow,
  getLearnersByCountry,
} from "../../../utils/dashboardAnalyticsHelpers"
import { computeInsights } from "../../../utils/insightsHelpers"

const AnalyticsPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadAnalytics = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    const [learnerTrend, topCourses, enrollmentByCategory, learnerMix, activeNow, learnersByCountry, insights] =
      await Promise.all([
        getLearnerTrend(orgId),
        getTopCourses(orgId),
        getEnrollmentByCategory(orgId),
        getLearnerMix(orgId),
        getActiveNow(orgId),
        getLearnersByCountry(orgId),
        computeInsights(orgId),
      ])
    setData({ learnerTrend, topCourses, enrollmentByCategory, learnerMix, activeNow, learnersByCountry, insights })
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    loadAnalytics()
  }, [loadAnalytics])

  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 6 })?.map((_, i) => (
          <div key={i} className="h-56 rounded-lg bg-muted animate-pulse md:col-span-2 lg:col-span-1" />
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      <InsightsStrip insights={data?.insights} loading={false} />
      <LearnersChart data={data?.learnerTrend?.data} deltaPercent={data?.learnerTrend?.deltaPercent} />
      <EnrolledNow count={data?.activeNow?.count} breakdown={data?.activeNow?.breakdown} />
      <TopCourses rows={data?.topCourses} />
      <TopCountries rows={data?.learnersByCountry?.rows} hasData={data?.learnersByCountry?.hasData} />
      <EnrollmentByCategory rows={data?.enrollmentByCategory} />
      <LearnerMix rows={data?.learnerMix} />
    </div>
  )
}

export default AnalyticsPanel